import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Form, Field, formValueSelector, change } from 'redux-form'
import { isEmpty, snakeCase } from 'lodash'
import classnames from 'classnames/bind'
import {
  Button,
  Modal,
  Icon,
  SelectFieldReduxFormWrapper,
  Text,
  utils,
} from '@adc/ui-core'
import { SOMEONE_ELSE } from '../multiCoBuyerForm/shared/constants'
import { MULTI_CO_BUYER_FORM_NAME } from '../multiCoBuyerForm'
import styles from './styles.scss'
const cx = classnames.bind(styles)
const { required } = utils.formValidators
const selector = formValueSelector(MULTI_CO_BUYER_FORM_NAME)

class RelationshipModal extends Component {
  constructor(props) {
    super(props)
    this.state.relationships = props.initialRelationships || {}
  }

  state = {
    relationships: {},
  }

  static propTypes = {
    onConfirm: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    elmId: PropTypes.string,
    // eslint-disable-next-line react/no-unused-prop-types
    fieldValues: PropTypes.any,
    names: PropTypes.arrayOf(
      PropTypes.shape({
        buyerId: PropTypes.string,
        name: PropTypes.string.isRequired,
        isDisplayedField: PropTypes.bool.isRequired,
      })
    ).isRequired,
    initialRelationships: PropTypes.objectOf(PropTypes.string),
  }

  getDefaultOptions = currentBuyerId => {
    const { names } = this.props
    const options = names
      .filter(({ buyerId }) => buyerId !== currentBuyerId)
      .map(n => ({
        text: n.name,
        value: n.buyerId,
        'data-elm-id': `option_${snakeCase(n.name)}`,
      }))
    return options
  }

  getAvailableOptions = currentBuyerId => {
    const { names } = this.props
    const { relationships } = this.state
    // filter myself and others with relationships
    return names
      .filter(
        ({ buyerId }) => buyerId !== currentBuyerId && !relationships[buyerId]
      )
      .map(({ name, buyerId }) => ({
        text: name,
        value: buyerId,
        'data-elm-id': `option_${snakeCase(name)}`,
      }))
  }

  getOptionsWhenInRelationships = currentBuyerId => {
    const { relationships } = this.state
    const { names } = this.props
    const relatedBuyerId = relationships[currentBuyerId]
    return names
      .filter(({ buyerId }) => buyerId === relatedBuyerId)
      .map(({ name, buyerId }) => ({
        text: name,
        value: buyerId,
        'data-elm-id': `option_${snakeCase(name)}`,
      }))
  }

  getRelationshipOptions = buyerId => {
    const { relationships } = this.state
    // no relationships set
    if (isEmpty(relationships)) {
      return this.getDefaultOptions(buyerId)
    }
    // buyerId shows up in a relationship
    if (relationships[buyerId] && relationships[buyerId] !== SOMEONE_ELSE) {
      return this.getOptionsWhenInRelationships(buyerId)
    } else {
      // relationships are set, but current buyerId is not in a relationship
      return this.getAvailableOptions(buyerId)
    }
  }

  setRelationshipToPerson = (buyerId, relationshipToBuyerId) => {
    const { relationships } = this.state
    const { dispatch } = this.props

    // modify the inverse in the form
    dispatch(
      change(
        MULTI_CO_BUYER_FORM_NAME,
        `relationships.${relationshipToBuyerId}`,
        buyerId
      )
    )

    // push the default and inverse
    relationships[buyerId] = relationshipToBuyerId
    relationships[relationshipToBuyerId] = buyerId
    this.setState({ relationships })
  }

  setRelationshipToOther = (buyerId, relationshipToBuyerId) => {
    const { relationships } = this.state
    // if relationshipTo is set to someone else don't set inverse
    relationships[buyerId] = relationshipToBuyerId
    this.setState({ relationships })
  }

  setClearingRelationship = buyerId => {
    const { relationships } = this.state
    const { dispatch } = this.props
    const relationshipToBuyerId = relationships[buyerId]

    // remove both buyerId index and the relationshipTo
    relationships[buyerId] = ''
    relationships[relationshipToBuyerId] = ''

    // fire redux-form action to remove from store
    dispatch(
      change(
        MULTI_CO_BUYER_FORM_NAME,
        `relationships.${relationshipToBuyerId}`,
        ''
      )
    )
    this.setState({ relationships })
  }

  onChangeRelationship = buyerId => {
    return (_, relationshipToBuyerId) => {
      if (relationshipToBuyerId && relationshipToBuyerId !== SOMEONE_ELSE) {
        // user sets to relationship to another person
        this.setRelationshipToPerson(buyerId, relationshipToBuyerId)
      } else if (
        relationshipToBuyerId &&
        relationshipToBuyerId === SOMEONE_ELSE
      ) {
        // if relationshipTo is set to someone else don't set inverse
        this.setRelationshipToOther(buyerId, relationshipToBuyerId)
      } else {
        // user clears selection; remove self and the inverse from relationships
        this.setClearingRelationship(buyerId)
      }
    }
  }

  render() {
    const {
      elmId,
      names,
      onClose,
      // eslint-disable-next-line react/prop-types
      open,
      // eslint-disable-next-line react/prop-types
      onConfirm,
    } = this.props
    return (
      <Modal
        open={open}
        onClose={onClose}
        elmId={elmId}
        mobileTitleSkin='light'
        closeIcon={
          <Icon
            className='close'
            data-elm-id={`${elmId}_close_button`}
            name='times'
            iconStyle='regular'
            fitted
            onClick={onClose}
          />
        }
      >
        <Modal.Header className={cx('header')}>
          <div className={cx('main-title')}>
            <Text
              as='h1'
              size='large'
              weight='heavy'
              data-elm-id={`${elmId}_header`}
            >
              Relationships
            </Text>
          </div>
          <div>
            <Text as='h2' data-elm-id={`${elmId}_sub_header`}>
              Connect the following Buyers to their Relationships
            </Text>
          </div>
        </Modal.Header>
        <Modal.Content className={cx('relationship')}>
          <div className={cx('ui', 'form')}>
            <Form onSubmit={onConfirm}>
              {names
                .filter(n => n.isDisplayedField)
                .map((n, index) => {
                  return (
                    <div className={cx('field-wrapper')} key={n.buyerId}>
                      <Field
                        name={`relationships.${n.buyerId}`}
                        component={SelectFieldReduxFormWrapper}
                        options={this.getRelationshipOptions(n.buyerId)}
                        label={
                          <Text
                            weight='heavy'
                            className={cx('label')}
                            data-elm-id={`${elmId}_buyer_label_${index}`}
                          >
                            {n.name} is Married To
                          </Text>
                        }
                        elmId={`${elmId}_buyer_${index}`}
                        onChange={this.onChangeRelationship(n.buyerId)}
                        key={n.buyerId}
                        selectOnBlur={false}
                        clearable
                        className={cx('field')}
                        validate={[
                          required({
                            msg: 'Relationship Required',
                          }),
                        ]}
                      />
                    </div>
                  )
                })}
              <div className={cx('actions')}>
                <div className={cx('cancel-btn', 'btn')}>
                  <Button
                    size='large'
                    role='secondary'
                    isStretch
                    onClick={onClose}
                    elmId={`${elmId}_cancel_btn`}
                  >
                    Cancel
                  </Button>
                </div>

                <div className={cx('submit-btn', 'btn')}>
                  <Button
                    type='submit'
                    size='large'
                    role='primary'
                    elmId={`${elmId}_submit_btn`}
                    isStretch
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </Form>
          </div>
        </Modal.Content>
      </Modal>
    )
  }
}

export default connect(state => {
  return {
    fieldValues: selector(state, MULTI_CO_BUYER_FORM_NAME),
  }
})(RelationshipModal)

export { MULTI_CO_BUYER_FORM_NAME }
